import { useCallback } from 'react';
import { CsvExportActionLifecycleArgs } from '@/components/action-buttons/csv-export-action.types';
import {
    CsvExportSelectionScope,
    resolveCsvExportSelectionScope,
} from '@/components/action-buttons/csv-export-selection-scope';
import {
    MarketplaceCsvDownloadArgs,
    MarketplaceCsvExportResult,
    useMarketplaceCsvExportAction,
} from '@/components/action-buttons/use-marketplace-csv-export-action';

type SelectionScopedCsvDownloadArgs = MarketplaceCsvDownloadArgs & {
    selectionScope: CsvExportSelectionScope;
};

type UseSelectionScopedMarketplaceCsvExportActionArgs = CsvExportActionLifecycleArgs & {
    accessToken: string;
    pickedIds: string[];
    selectionSessionId: string;
    runSelectionScopedCsvDownload: (args: SelectionScopedCsvDownloadArgs) => Promise<MarketplaceCsvExportResult>;
};

type UseSelectionScopedMarketplaceCsvExportActionResult = {
    exportCsv: () => Promise<void>;
    cancelExport: () => void;
};

export const useSelectionScopedMarketplaceCsvExportAction = ({
    accessToken,
    pickedIds,
    selectionSessionId,
    isExportLoading,
    startExport,
    finishExportSuccess,
    finishExportFailure,
    finishExportCanceled,
    runSelectionScopedCsvDownload,
}: UseSelectionScopedMarketplaceCsvExportActionArgs): UseSelectionScopedMarketplaceCsvExportActionResult => {
    const runCsvDownload = useCallback(
        ({ accessToken, exportOperationId, signal }: MarketplaceCsvDownloadArgs) => {
            const selectionScope = resolveCsvExportSelectionScope({
                pickedIds,
                selectionSessionId,
            });

            return runSelectionScopedCsvDownload({
                accessToken,
                exportOperationId,
                signal,
                selectionScope,
            });
        },
        [pickedIds, runSelectionScopedCsvDownload, selectionSessionId],
    );

    return useMarketplaceCsvExportAction({
        accessToken,
        isExportLoading,
        startExport,
        finishExportSuccess,
        finishExportFailure,
        finishExportCanceled,
        runCsvDownload,
    });
};
